import type { Component } from "vue";
import type { PredefinedNotification, ReasonNotPossible } from "../Api/predefinedNotifications";
import { convertToTimezoneDateTimeMed } from "../Common/dateTimeUtils";
import urls from "../Urls/urls";
import EnableDeadlineStep from "./SingleCompetitorCategories/EnableDeadlineStep.vue";

export type ReasonDescription = {
    messageKey: string;
    /**
     * Replaces the message when the organizer can resolve the reason right in the modal. Props: `data`, `eventId`,
     * `eventTimezone`; emits `resolved` and the modal then reloads the preview.
     */
    component?: Component;
    /** Values for the `{0}`, `{1}` placeholders of the message. */
    params?: (data: PredefinedNotification, eventTimezone: string) => string[];
    /** The message names another person, so it has to be masked in session recordings. */
    containsPii?: boolean;
    cta?: {
        labelKey: string;
        href: (eventId: number) => string;
    };
};

export const GENERIC_REASON: ReasonDescription = {
    messageKey: "predefined_notification.not_possible_generic",
};

export const reasons: Partial<Record<ReasonNotPossible, ReasonDescription>> = {
    noEmailContactData: {
        messageKey: "predefined_notification.no_contact",
        cta: {
            labelKey: "predefined_notification.click_to_edit",
            href: eventId => urls.eventAdmin().editEventPageWithContactInfo(eventId),
        },
    },
    noRegistrationStage: {
        messageKey: "predefined_notification.no_active_stage",
    },
    locked: { messageKey: "predefined_notification.locked_for_24h" },
    noReceivers: { messageKey: "predefined_notification.no_competitors" },
    noDeadline: { messageKey: "predefined_notification.not_possible_generic", component: EnableDeadlineStep },
    registrationOpen: {
        messageKey: "predefined_notification.registration_open",
        cta: {
            labelKey: "page.registration_stage.manage_stages",
            href: eventId => urls.eventAdmin().editEventPageWithRegistrationStages(eventId),
        },
    },
    noSingleCompetitorCategories: { messageKey: "predefined_notification.no_single_competitor_categories" },
    alreadySent: {
        messageKey: "predefined_notification.already_sent",
        containsPii: true,
        params: (data, eventTimezone) =>
            data.previousRequest
                ? [convertToTimezoneDateTimeMed(data.previousRequest.requestedAt, eventTimezone), data.previousRequest.requestedBy]
                : ["", ""],
    },
};
